import { useState, useRef, useEffect, useMemo } from 'react';
import {
  MessageCircle,
  CheckSquare,
  Calendar,
  Link2,
  MoreHorizontal,
  X,
  Phone,
  Mail,
  Bell,
  StickyNote,
  Zap,
} from 'lucide-react';
import './actions.css';
import { useTranslation } from '../../hooks';
import { STANDARD_FAST_ACTIONS, type ActionType as RowActionType } from '../../types/row.types';

/**
 * FastActions - Sistema universale di azioni rapide
 *
 * Le FastActions sono connessioni peer-to-peer tra entità.
 * Ogni azione DEVE avere una destinazione reale nel sistema.
 *
 * Standard actions (same everywhere):
 * - task: Crea Task collegato → projectsStore.createTask
 * - event: Crea Evento collegato → calendarStore.createEvent
 * - email: Invia email → hubStore
 * - call: Registra chiamata → hubStore
 * - message: Invia messaggio (SMS, WhatsApp, Telegram) → hubStore/chatStore
 * - reminder: Crea promemoria → calendarStore
 * - note: Aggiungi nota → notesStore
 *
 * Additional actions:
 * - connect: Collega ad altra entità → linksStore.createLink
 */
export type ActionType = RowActionType | 'connect';

interface FastActionsProps {
  /** Entity ID to link actions to */
  entityId: string;
  /** Entity type for context */
  entityType: string;
  /** Available actions (default: STANDARD_FAST_ACTIONS) */
  actions?: ActionType[];
  /** Callback when action is triggered */
  onAction?: (action: ActionType, entityId: string) => void;
  /** Compact mode (icons only) */
  compact?: boolean;
  /** Vertical layout */
  vertical?: boolean;
}

// Base action config with icons and colors (labels are translated per-component)
const actionBaseConfig: Record<ActionType, { icon: typeof CheckSquare; color: string }> = {
  task: { icon: CheckSquare, color: '#f59e0b' },
  event: { icon: Calendar, color: '#ec4899' },
  email: { icon: Mail, color: '#3b82f6' },
  call: { icon: Phone, color: '#16a34a' },
  message: { icon: MessageCircle, color: '#22c55e' },
  reminder: { icon: Bell, color: '#ef4444' },
  note: { icon: StickyNote, color: '#a855f7' },
  connect: { icon: Link2, color: '#06b6d4' },
  videocall: { icon: MessageCircle, color: '#8b5cf6' },
  file: { icon: StickyNote, color: '#6b7280' },
  invoice: { icon: StickyNote, color: '#0891b2' },
};

// Hook to get translated action config
function useActionConfig() {
  const { t } = useTranslation();
  return useMemo(() => {
    const config: Record<ActionType, { icon: typeof CheckSquare; label: string; color: string }> = {} as any;
    for (const [key, value] of Object.entries(actionBaseConfig)) {
      config[key as ActionType] = {
        ...value,
        label: t(`actions.${key}`),
      };
    }
    return config;
  }, [t]);
}

// Legacy actionConfig for backwards compatibility (uses Italian labels as fallback)
const actionConfig: Record<ActionType, { icon: typeof CheckSquare; label: string; color: string }> = {
  task: { icon: CheckSquare, label: 'Task', color: '#f59e0b' },
  event: { icon: Calendar, label: 'Event', color: '#ec4899' },
  email: { icon: Mail, label: 'Email', color: '#3b82f6' },
  call: { icon: Phone, label: 'Call', color: '#16a34a' },
  message: { icon: MessageCircle, label: 'Message', color: '#22c55e' },
  reminder: { icon: Bell, label: 'Reminder', color: '#ef4444' },
  note: { icon: StickyNote, label: 'Note', color: '#a855f7' },
  connect: { icon: Link2, label: 'Connect', color: '#06b6d4' },
  videocall: { icon: MessageCircle, label: 'Video call', color: '#8b5cf6' },
  file: { icon: StickyNote, label: 'File', color: '#6b7280' },
  invoice: { icon: StickyNote, label: 'Invoice', color: '#0891b2' },
};

// Export config for use in other components
export { actionConfig };

// Default to standard fast actions
const defaultActions: ActionType[] = [...STANDARD_FAST_ACTIONS];

/**
 * FastActions - Quick action buttons for entities
 * Provides one-click access to common actions like calling, emailing, creating tasks
 */
export function FastActions({
  entityId,
  entityType,
  actions = defaultActions,
  onAction,
  compact = false,
  vertical = false,
}: FastActionsProps) {
  const { t } = useTranslation();
  const translatedConfig = useActionConfig();
  const [expanded, setExpanded] = useState(false);
  const visibleActions = compact && !expanded ? actions.slice(0, 3) : actions;
  const hasMore = compact && actions.length > 3;

  const handleAction = (action: ActionType) => {
    onAction?.(action, entityId);
  };

  return (
    <div
      className={`fast-actions ${compact ? 'fast-actions--compact' : ''} ${vertical ? 'fast-actions--vertical' : ''}`}
    >
      {visibleActions.map((action) => {
        const config = translatedConfig[action];
        const Icon = config.icon;

        return (
          <button
            key={action}
            className="fast-action-btn"
            onClick={(e) => {
              e.stopPropagation();
              handleAction(action);
            }}
            title={config.label}
            style={{ '--action-color': config.color } as React.CSSProperties}
          >
            <Icon size={compact ? 14 : 16} />
            {!compact && <span className="fast-action-label">{config.label}</span>}
          </button>
        );
      })}

      {hasMore && !expanded && (
        <button
          className="fast-action-btn fast-action-more"
          onClick={(e) => {
            e.stopPropagation();
            setExpanded(true);
          }}
          title={t('actions.moreActions')}
        >
          <MoreHorizontal size={14} />
        </button>
      )}

      {hasMore && expanded && (
        <button
          className="fast-action-btn fast-action-close"
          onClick={(e) => {
            e.stopPropagation();
            setExpanded(false);
          }}
          title={t('common.close')}
        >
          <X size={14} />
        </button>
      )}
    </div>
  );
}

/**
 * ActionButton - Single action button with hover tooltip
 */
interface ActionButtonProps {
  action: ActionType;
  onClick: () => void;
  size?: 'sm' | 'md' | 'lg';
  showLabel?: boolean;
}

export function ActionButton({ action, onClick, size = 'md', showLabel = false }: ActionButtonProps) {
  const translatedConfig = useActionConfig();
  const config = translatedConfig[action];
  const Icon = config.icon;

  // Icon sizes increased by 20%
  const iconSize = size === 'sm' ? 17 : size === 'lg' ? 24 : 19;

  return (
    <button
      className={`action-button action-button--${size}`}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      title={config.label}
      style={{ '--action-color': config.color } as React.CSSProperties}
    >
      <Icon size={iconSize} />
      {showLabel && <span className="action-button-label">{config.label}</span>}
    </button>
  );
}

// =============================================================================
// FAST ACTIONS TRIGGER
// =============================================================================

/**
 * FastActionsTrigger - Componente standard per le fast actions
 *
 * Mostra un'icona fulmine che, al hover/click, rivela una barra
 * con le icone delle azioni in overlay con animazione staggered.
 *
 * Caratteristiche:
 * - Barra unica con icone (non cerchi individuali)
 * - Icone appaiono a sinistra del trigger (o destra con position="right")
 * - Animazione staggered (una per una, dalla più vicina)
 * - Resta aperto 1 secondo dopo che il mouse esce
 *
 * Questo è il componente STANDARD da usare ovunque:
 * - Drawer items (sidebar)
 * - Chat messages
 * - Task rows
 * - Any entity row
 *
 * @example
 * ```tsx
 * <FastActionsTrigger
 *   entityId="tsk-123"
 *   onAction={(action) => console.log('Action:', action)}
 * />
 * ```
 */
interface FastActionsTriggerProps {
  /** Entity ID to link actions to */
  entityId: string;
  /** Available actions (default: STANDARD_FAST_ACTIONS) */
  actions?: ActionType[];
  /** Callback when action is triggered */
  onAction?: (action: ActionType, entityId: string) => void;
  /** Size of the trigger icon */
  size?: 'sm' | 'md' | 'lg';
  /** Position of icons relative to trigger */
  position?: 'left' | 'right';
}

export function FastActionsTrigger({
  entityId,
  actions = defaultActions,
  onAction,
  size = 'sm',
  position = 'left',
}: FastActionsTriggerProps) {
  const { t } = useTranslation();
  const translatedConfig = useActionConfig();
  const [isOpen, setIsOpen] = useState(false);
  const [visibleIcons, setVisibleIcons] = useState<number[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const animationTimeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  // Icon sizes
  const iconSize = size === 'sm' ? 17 : size === 'lg' ? 24 : 19;
  const actionIconSize = 16;

  // Clear all animation timeouts
  const clearAnimationTimeouts = () => {
    animationTimeoutsRef.current.forEach(clearTimeout);
    animationTimeoutsRef.current = [];
  };

  // Handle opening with staggered animation
  const handleOpen = () => {
    // Clear any pending close timeout
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }

    if (isOpen) return;

    setIsOpen(true);
    setVisibleIcons([]);

    // Staggered animation: show icons one by one (closest to trigger first)
    clearAnimationTimeouts();
    actions.forEach((_, index) => {
      const timeout = setTimeout(() => {
        setVisibleIcons(prev => [...prev, index]);
      }, index * 50); // 50ms stagger between each icon
      animationTimeoutsRef.current.push(timeout);
    });
  };

  // Handle closing with delay
  const handleClose = () => {
    // Clear any existing close timeout
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
    }

    // Wait 1 second before closing
    closeTimeoutRef.current = setTimeout(() => {
      clearAnimationTimeouts();
      setIsOpen(false);
      setVisibleIcons([]);
      closeTimeoutRef.current = null;
    }, 1000);
  };

  // Cancel close if mouse re-enters
  const handleMouseEnter = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    handleOpen();
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
      clearAnimationTimeouts();
    };
  }, []);

  const handleAction = (action: ActionType) => {
    onAction?.(action, entityId);
    // Close immediately after action
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
    }
    clearAnimationTimeouts();
    setIsOpen(false);
    setVisibleIcons([]);
  };

  return (
    <div
      ref={containerRef}
      style={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleClose}
    >
      {/* Action icons bar - appears to the left (or right) of trigger */}
      {isOpen && (
        <div
          style={{
            position: 'absolute',
            [position === 'left' ? 'right' : 'left']: '100%',
            [position === 'left' ? 'marginRight' : 'marginLeft']: '4px',
            display: 'flex',
            flexDirection: position === 'left' ? 'row-reverse' : 'row',
            alignItems: 'center',
            gap: '2px',
            padding: '4px 6px',
            backgroundColor: 'var(--surface, #fff)',
            borderRadius: '6px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
          }}
        >
          {actions.map((action, index) => {
            const config = translatedConfig[action];
            if (!config) return null;
            const Icon = config.icon;
            const isVisible = visibleIcons.includes(index);

            return (
              <button
                key={action}
                onClick={(e) => {
                  e.stopPropagation();
                  handleAction(action);
                }}
                title={config.label}
                style={{
                  padding: '4px',
                  border: 'none',
                  background: 'transparent',
                  cursor: 'pointer',
                  color: 'var(--text-muted, #6b7280)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '4px',
                  opacity: isVisible ? 1 : 0,
                  transform: isVisible ? 'translateX(0)' : `translateX(${position === 'left' ? '-8px' : '8px'})`,
                  transition: 'all 0.15s ease',
                  flexShrink: 0,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = config.color;
                  e.currentTarget.style.backgroundColor = `${config.color}15`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = 'var(--text-muted, #6b7280)';
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <Icon size={actionIconSize} />
              </button>
            );
          })}
        </div>
      )}

      {/* Trigger button (lightning bolt) */}
      <button
        className="fast-actions-trigger"
        onClick={(e) => {
          e.stopPropagation();
          if (isOpen) {
            if (closeTimeoutRef.current) {
              clearTimeout(closeTimeoutRef.current);
            }
            clearAnimationTimeouts();
            setIsOpen(false);
            setVisibleIcons([]);
          } else {
            handleOpen();
          }
        }}
        title={t('actions.quickActions')}
        style={{
          padding: '4px',
          border: 'none',
          background: 'transparent',
          cursor: 'pointer',
          color: isOpen ? 'var(--primary, #3b82f6)' : 'var(--text-muted, #9ca3af)',
          display: 'flex',
          alignItems: 'center',
          borderRadius: '4px',
          transition: 'color 0.15s ease',
        }}
      >
        <Zap size={iconSize} fill={isOpen ? 'currentColor' : 'none'} />
      </button>
    </div>
  );
}
